import { ReactNode, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "../ui/command";
import { cn } from "../../lib/utils";

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SearchableSelectOption[];
  /** Label + value for the "show all" entry (selected when value === "all"). */
  allLabel?: string;
  icon?: ReactNode;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Combobox: a Select with a built-in search box. Handles long option lists
 * (e.g. hundreds/thousands of schools) by filtering as you type. Value "all"
 * represents the no-filter / show-all state.
 */
export function SearchableSelect({
  value,
  onValueChange,
  options,
  allLabel = "All",
  icon,
  placeholder = "Select",
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  disabled,
  className,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);

  const selectedLabel =
    value === "all"
      ? allLabel
      : options.find((o) => o.value === value)?.label ?? placeholder;

  const handleSelect = (next: string) => {
    onValueChange(next);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("justify-between font-normal", className)}
        >
          <span className="flex items-center gap-2 truncate">
            {icon}
            <span className="truncate">{selectedLabel}</span>
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandItem value={allLabel} onSelect={() => handleSelect("all")}>
              <Check
                className={cn(
                  "h-4 w-4",
                  value === "all" ? "opacity-100" : "opacity-0"
                )}
              />
              {allLabel}
            </CommandItem>
            {options.map((option) => (
              <CommandItem
                key={option.value}
                value={`${option.label} ${option.value}`}
                onSelect={() => handleSelect(option.value)}
              >
                <Check
                  className={cn(
                    "h-4 w-4",
                    value === option.value ? "opacity-100" : "opacity-0"
                  )}
                />
                <span className="truncate">{option.label}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
